import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  CircularProgress,
  Chip,
  Alert,
} from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { useUserStore } from 'store';
import { formatPriceWithCurrency, priceForDisplay } from '../../utils/vat';

/**
 * Reconcile a picked catalogue item against a part the workshop already stocks.
 *
 * When the workshop has an inventory-tracked part with the same code, picking the
 * catalogue item should reuse that part (so completion deducts stock) and offer to
 * update its purchase/sell price to the catalogue's. For a cross-vendor match the
 * user first confirms it is the same product.
 *
 * Props:
 *   open            - whether the dialog is shown
 *   option          - the picked catalogue typeahead option (item_id, source_code, ...)
 *   match           - { part, match_basis, is_tracked, cost_differs, sell_differs }
 *   cataloguePrices - { purchase_price, list_price, list_price_is_sell, sell_price }
 *   onResolved(part)- called with the resolved Part once the choice is applied
 *   onClose()       - called to dismiss without resolving
 */
export default function CatalogueStockReconcileDialog({ open, option, match, cataloguePrices, onResolved, onClose }) {
  const { t } = useTranslation();
  const { currentOrganization } = useUserStore();
  // Both sides are ex-VAT (catalogue prices are normalized at import, Part.price
  // is net). Display them in the organization's price display mode so they match
  // the parts registry; the PATCH below always sends the net values.
  const showInclVat = currentOrganization?.default_price_includes_vat ?? false;
  const display = (value) => formatPriceWithCurrency(priceForDisplay(value, showInclVat));
  const isCrossVendor = match?.match_basis === 'code_tracked';
  const [useStocked, setUseStocked] = useState(true);
  const [updateCost, setUpdateCost] = useState(false);
  const [updateSell, setUpdateSell] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!open || !match || !option) return null;

  const part = match.part;
  const stock = part.stock_info;
  const catCost = cataloguePrices?.purchase_price != null ? parseFloat(cataloguePrices.purchase_price) : null;
  const catSell = cataloguePrices?.sell_price != null ? parseFloat(cataloguePrices.sell_price) : null;
  const curCost = part.price != null ? parseFloat(part.price) : null;
  const curSell = part.sell_price != null ? parseFloat(part.sell_price) : null;
  const canUpdateCost = catCost != null && match.cost_differs;
  // Margin-VAT parts store sell_price as the gross customer price; a net list
  // price must never be written there.
  const canUpdateSell = catSell != null && match.sell_differs && !part.is_margin_vat_scheme;
  const showPriceSection = !(isCrossVendor && !useStocked) && (canUpdateCost || canUpdateSell);

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      let resolvedPart;
      if (isCrossVendor && !useStocked) {
        // User says it's a different product → materialize a fresh catalogue part.
        const matRes = await axios.post(`/api/catalogue-items/${option.item_id}/materialize/`);
        resolvedPart = matRes.data?.part;
      } else {
        // Reuse the stocked part (adopts it into the catalogue) so completion deducts stock.
        const matRes = await axios.post(`/api/catalogue-items/${option.item_id}/materialize/`, {
          reuse_part_id: part.id,
        });
        resolvedPart = matRes.data?.part;
        const patch = {};
        if (updateCost && canUpdateCost) {
          patch.price = cataloguePrices.purchase_price;
          patch.price_entered_with_vat = false;
          patch.stored_price_incl_vat = null;
        }
        if (updateSell && canUpdateSell) {
          patch.sell_price = cataloguePrices.sell_price;
          patch.sell_price_entered_with_vat = false;
          patch.stored_sell_price_incl_vat = null;
        }
        if (Object.keys(patch).length) {
          const patchRes = await axios.patch(`/api/parts/${part.id}/`, patch);
          resolvedPart = patchRes.data;
        }
      }
      if (resolvedPart) onResolved(resolvedPart);
    } catch {
      setError(t('catalogues.materializeFailed'));
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 }, 'data-testid': 'catalogue-stock-reconcile-dialog' }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Inventory2OutlinedIcon color="primary" />
        {t('catalogues.stockReconcileTitle')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {t('catalogues.alreadyInStockBody', {
            name: part.name,
            code: part.manufacturer_code || option.source_code || '',
          })}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
          {part.vendor_name && <Chip size="small" label={part.vendor_name} variant="outlined" />}
          {stock && (
            <Chip
              size="small"
              color="success"
              variant="outlined"
              label={t('catalogues.stockSaldoChip', { qty: stock.quantity_free })}
            />
          )}
        </Box>

        {isCrossVendor && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              {t('catalogues.sameProductQuestion')}
            </Typography>
            <RadioGroup
              value={useStocked ? 'stocked' : 'new'}
              onChange={(e) => setUseStocked(e.target.value === 'stocked')}
            >
              <FormControlLabel
                value="stocked"
                control={<Radio size="small" />}
                label={t('catalogues.useStockedPart')}
                data-testid="reconcile-use-stocked"
              />
              <FormControlLabel
                value="new"
                control={<Radio size="small" />}
                label={t('catalogues.useAsNew')}
                data-testid="reconcile-use-new"
              />
            </RadioGroup>
          </Box>
        )}

        {showPriceSection && (
          <Box sx={{ backgroundColor: '#f7f9fc', borderRadius: 1, p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              {t('catalogues.priceReconcileHeading')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }} data-testid="reconcile-vat-basis">
              {showInclVat ? t('Prices include VAT') : t('catalogues.pricesExVatNote')}
            </Typography>
            <Table size="small" sx={{ '& td': { py: 0.5, px: 1, border: 'none' } }}>
              <TableBody>
                <TableRow>
                  <TableCell />
                  <TableCell sx={{ fontWeight: 600 }}>{t('catalogues.currentPrice')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('catalogues.cataloguePrice')}</TableCell>
                </TableRow>
                {canUpdateCost && (
                  <TableRow>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Checkbox size="small" checked={updateCost} onChange={(e) => setUpdateCost(e.target.checked)} />
                        }
                        label={t('catalogues.updateCost')}
                        data-testid="reconcile-update-cost"
                      />
                    </TableCell>
                    <TableCell>{curCost != null ? display(curCost) : '–'}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{display(catCost)}</TableCell>
                  </TableRow>
                )}
                {canUpdateSell && (
                  <TableRow>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Checkbox size="small" checked={updateSell} onChange={(e) => setUpdateSell(e.target.checked)} />
                        }
                        label={t('catalogues.updateSell')}
                        data-testid="reconcile-update-sell"
                      />
                    </TableCell>
                    <TableCell>{curSell != null ? display(curSell) : '–'}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{display(catSell)}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
          data-testid="reconcile-confirm"
        >
          {t('catalogues.confirmReconcile')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
